import React, { createContext, useContext, useMemo } from 'react';
import { usePersistentState } from '../hooks/usePersistentState.js';

// Meals the user wants to make later but isn't ordering right now. Persisted
// since Stage 5 — a queue that emptied itself when you closed the tab was a
// list you could not plan with.
//
// A planned meal is not a saved recipe: saving lives on the recipe itself as
// `savedAt` in RecipeLibraryContext, and recipe search deliberately does not
// read the queue. `recipeId` here is nullable and stays null for a genuine
// idea that has never been resolved into a recipe.

const QueueContext = createContext(null);

const SEED = [
  {
    id: 'q1',
    recipeId: 'miso-ramen',
    mealName: 'Miso ramen',
    addedAt: Date.now() - 1000 * 60 * 60 * 26,
    sourceInput: 'https://videos.example.com/shorts/miso-ramen',
    position: 0,
  },
  {
    id: 'q2',
    recipeId: null,
    mealName: 'Chicken shawarma bowls',
    addedAt: Date.now() - 1000 * 60 * 60 * 3,
    sourceInput: 'Chicken shawarma bowls with garlic sauce and pickles',
    position: 1,
  },
  {
    id: 'q3',
    recipeId: null,
    mealName: 'Weeknight veggie stir fry',
    addedAt: Date.now() - 1000 * 60 * 20,
    sourceInput: 'Quick veggie stir fry with whatever is in the fridge',
    position: 2,
  },
];

function nextId() {
  return `q${Math.random().toString(36).slice(2, 9)}`;
}

// A short, human name guessed from raw input — real parsing only happens
// once a meal is actually sent through Stage 1's resolve step.
function guessMealName(sourceInput) {
  if (/^https?:\/\//i.test(sourceInput)) return 'Video idea';
  const words = sourceInput.split(/\s+/).slice(0, 6).join(' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function QueueProvider({ children }) {
  const [items, setItems] = usePersistentState('queue', SEED);

  const value = useMemo(() => {
    const sorted = [...items].sort((a, b) => a.position - b.position);

    return {
      items: sorted,

      addToQueue: (sourceInput, { recipeId = null, mealName = null } = {}) => {
        const trimmed = sourceInput.trim();
        if (!trimmed) return;
        setItems((prev) => [
          ...prev,
          {
            id: nextId(),
            recipeId,
            mealName: mealName || guessMealName(trimmed),
            addedAt: Date.now(),
            sourceInput: trimmed,
            position: prev.length,
          },
        ]);
      },

      removeFromQueue: (id) => {
        setItems((prev) =>
          prev.filter((item) => item.id !== id).map((item, index) => ({ ...item, position: index }))
        );
      },

      moveItem: (id, direction) => {
        setItems((prev) => {
          const sortedPrev = [...prev].sort((a, b) => a.position - b.position);
          const index = sortedPrev.findIndex((item) => item.id === id);
          const swapWith = index + direction;
          if (index === -1 || swapWith < 0 || swapWith >= sortedPrev.length) return prev;
          const next = [...sortedPrev];
          [next[index], next[swapWith]] = [next[swapWith], next[index]];
          return next.map((item, i) => ({ ...item, position: i }));
        });
      },

      // Removes the item and hands it back so the caller can pre-fill
      // Stage 1 with its sourceInput.
      takeItem: (id) => {
        const item = items.find((i) => i.id === id) || null;
        if (item) {
          setItems((prev) =>
            prev.filter((i) => i.id !== id).map((i, index) => ({ ...i, position: index }))
          );
        }
        return item;
      },
    };
  }, [items]);

  return <QueueContext.Provider value={value}>{children}</QueueContext.Provider>;
}

export function useQueue() {
  const context = useContext(QueueContext);
  if (!context) throw new Error('useQueue must be used inside <QueueProvider>');
  return context;
}
